import { Head, Link, router } from '@inertiajs/react'
import { Plus, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { ColumnHeader } from '@/components/ColumnHeader'
import { PageHeader } from '@/components/PageHeader'
import { Pagination } from '@/components/Pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { companiesPath, companyPath, newCompanyPath } from '@/lib/routes'
import { useRememberThisUrl } from '@/lib/rememberedUrl'
import { useDebouncedCallback } from '@/lib/useDebouncedCallback'
import type { PaginationMeta } from '@/types/inertia'

type CompanyStatus = 'operating' | 'raising' | 'exited_ma' | 'exited_ipo' | 'dissolved'

type Country = { code: string; name: string; flag: string }

type IndustryTag = { id: number; name: string; emoji: string | null }

type CompanyRow = {
  id: number
  slug: string
  name: string
  oneliner: string | null
  industries: IndustryTag[]
  country: Country | null
  status: CompanyStatus
  logo_url: string | null
  batch_code: string | null
}

type FilterState = {
  name: string | null
  industry_id: string[]
  status: CompanyStatus[]
  country: string[]
  batch_id: string[]
}

type SortState = {
  column: string
  direction: 'asc' | 'desc'
}

type LabeledOption = { value: string; label: string }

type FilterOptions = {
  industries: IndustryTag[]
  countries: LabeledOption[]
  statuses: CompanyStatus[]
  batches: { id: number; code: string; label: string }[]
}

type CompaniesIndexProps = {
  companies: CompanyRow[]
  pagination: PaginationMeta
  filters: FilterState
  sort: SortState
  filter_options: FilterOptions
}

const STATUS_LABEL: Record<CompanyStatus, string> = {
  operating: 'Operating',
  raising: 'Raising',
  exited_ma: 'Exited (M&A)',
  exited_ipo: 'Exited (IPO)',
  dissolved: 'Dissolved',
}

const STATUS_VARIANT: Record<CompanyStatus, 'default' | 'accent' | 'secondary' | 'destructive'> = {
  operating: 'default',
  raising: 'accent',
  exited_ma: 'secondary',
  exited_ipo: 'secondary',
  dissolved: 'destructive',
}

type QueryParams = Record<string, string | string[] | null>

function buildQuery(filters: FilterState, sort: SortState): QueryParams {
  const sortParam = sort.direction === 'desc' ? `-${sort.column}` : sort.column
  return {
    name: filters.name || null,
    industry_id: filters.industry_id.length ? filters.industry_id : null,
    status: filters.status.length ? filters.status : null,
    country: filters.country.length ? filters.country : null,
    batch_id: filters.batch_id.length ? filters.batch_id : null,
    sort: sortParam === 'name' ? null : sortParam,
  }
}

// Props that depend on the current query. filter_options is a lazy prop
// (the dropdown option lists don't change when you filter/sort/search),
// so we keep the initial value around and skip fetching it on partial
// reloads. The server's `-> { filter_options }` lambda isn't called.
const PARTIAL_RELOAD_KEYS = ['companies', 'pagination', 'filters', 'sort']

function visitWith(query: QueryParams) {
  const clean: Record<string, string | string[]> = {}
  Object.entries(query).forEach(([k, v]) => {
    if (v !== null) clean[k] = v
  })
  router.get(companiesPath(), clean, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
    only: PARTIAL_RELOAD_KEYS,
  })
}

function Chip({ label, value, onRemove }: { label: string; value: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} ${value}`}
        className="flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

function CompaniesIndex({
  companies,
  pagination,
  filters,
  sort,
  filter_options: filterOptions,
}: CompaniesIndexProps) {
  useRememberThisUrl('companies')

  const [nameInput, setNameInput] = useState(filters.name ?? '')

  // Keep the input in sync if the URL changes from elsewhere (back button,
  // clearing via chips, etc.)
  useEffect(() => {
    setNameInput(filters.name ?? '')
  }, [filters.name])

  // Debounced search. Inertia's router cancels in-flight requests to the
  // same URL when a newer one starts, so we don't need to track tokens
  // ourselves — only the timer.
  const searchDebounced = useDebouncedCallback((nextName: string) => {
    visitWith(buildQuery({ ...filters, name: nextName || null }, sort))
  }, 300)

  const onNameChange = (value: string) => {
    setNameInput(value)
    searchDebounced(value)
  }

  const clearName = () => {
    setNameInput('')
    searchDebounced.cancel()
    visitWith(buildQuery({ ...filters, name: null }, sort))
  }

  const setFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    visitWith(buildQuery({ ...filters, [key]: value }, sort))
  }

  const setSort = (column: string, direction: 'asc' | 'desc') => {
    visitWith(buildQuery(filters, { column, direction }))
  }

  const clearAll = () => {
    setNameInput('')
    searchDebounced.cancel()
    visitWith({})
  }

  const hasFilters = useMemo(() => {
    return (
      Boolean(filters.name) ||
      filters.industry_id.length > 0 ||
      filters.status.length > 0 ||
      filters.country.length > 0 ||
      filters.batch_id.length > 0
    )
  }, [filters])

  const paginationQuery = buildQuery(filters, sort)

  // Option shapes for the ColumnHeader filter sections.
  const industryOptions = filterOptions.industries.map((i) => ({
    value: String(i.id),
    label: i.emoji ? `${i.emoji}  ${i.name}` : i.name,
  }))
  const industryLabelById = useMemo(
    () => new Map(filterOptions.industries.map((i) => [String(i.id), i])),
    [filterOptions.industries],
  )
  const countryOptions = filterOptions.countries
  const statusOptions = filterOptions.statuses.map((s) => ({
    value: s,
    label: STATUS_LABEL[s],
  }))
  const batchOptions = filterOptions.batches.map((b) => ({
    value: String(b.id),
    label: b.code,
  }))

  const batchLabelById = useMemo(
    () => new Map(filterOptions.batches.map((b) => [String(b.id), b.code])),
    [filterOptions.batches],
  )

  return (
    <>
      <Head title="Companies" />

      <div className="mx-auto max-w-6xl px-8 py-10">
        <PageHeader
          title="Companies"
          description="The portfolio — operating, raising, exited, and dissolved."
          actions={
            <Link href={newCompanyPath()}>
              <Button>
                <Plus />
                New company
              </Button>
            </Link>
          }
        />

        {/* Hero search */}
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-12 pl-11 pr-12 text-base"
            placeholder="Search companies by name…"
            value={nameInput}
            onChange={(e) => onNameChange(e.target.value)}
          />
          {nameInput && (
            <button
              type="button"
              onClick={clearName}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {hasFilters && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {filters.industry_id.map((v) => {
              const i = industryLabelById.get(v)
              return (
                <Chip
                  key={`ind-${v}`}
                  label="Industry"
                  value={i ? (i.emoji ? `${i.emoji}  ${i.name}` : i.name) : v}
                  onRemove={() =>
                    setFilter('industry_id', filters.industry_id.filter((x) => x !== v))
                  }
                />
              )
            })}
            {filters.status.map((v) => (
              <Chip
                key={`st-${v}`}
                label="Status"
                value={STATUS_LABEL[v]}
                onRemove={() => setFilter('status', filters.status.filter((x) => x !== v))}
              />
            ))}
            {filters.batch_id.map((v) => (
              <Chip
                key={`b-${v}`}
                label="Batch"
                value={batchLabelById.get(v) ?? v}
                onRemove={() => setFilter('batch_id', filters.batch_id.filter((x) => x !== v))}
              />
            ))}
            {filters.country.map((v) => {
              const opt = countryOptions.find((o) => o.value === v)
              return (
                <Chip
                  key={`c-${v}`}
                  label="Country"
                  value={opt?.label ?? v}
                  onRemove={() => setFilter('country', filters.country.filter((x) => x !== v))}
                />
              )
            })}
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="mb-3 flex items-center justify-end text-xs text-muted-foreground">
          <span className="tabular-nums">{pagination.total_count} total</span>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader column="name" label="Name" sort={sort} onSort={setSort} />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader
                      column="industries"
                      label="Industries"
                      sort={sort}
                      onSort={setSort}
                      filterLabel="Industry"
                      filterOptions={industryOptions}
                      filterValue={filters.industry_id}
                      onFilterChange={(v) => setFilter('industry_id', v)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader
                      column="batch"
                      label="Batch"
                      sort={sort}
                      onSort={setSort}
                      filterLabel="Batch"
                      filterOptions={batchOptions}
                      filterValue={filters.batch_id}
                      onFilterChange={(v) => setFilter('batch_id', v)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader
                      column="country"
                      label="Country"
                      sort={sort}
                      onSort={setSort}
                      filterLabel="Country"
                      filterOptions={countryOptions}
                      filterValue={filters.country}
                      onFilterChange={(v) => setFilter('country', v)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader
                      column="status"
                      label="Status"
                      sort={sort}
                      onSort={setSort}
                      filterLabel="Status"
                      filterOptions={statusOptions}
                      filterValue={filters.status}
                      onFilterChange={(v) => setFilter('status', v as CompanyStatus[])}
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {companies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center">
                      <p className="text-sm text-muted-foreground">
                        {hasFilters
                          ? 'No companies match these filters.'
                          : 'No companies yet.'}
                      </p>
                    </td>
                  </tr>
                )}
                {companies.map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => router.visit(companyPath(c.slug))}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-muted">
                          {c.logo_url ? (
                            <img
                              src={c.logo_url}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <span className="text-[10px] text-muted-foreground">
                              {c.name.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium">{c.name}</div>
                          {c.oneliner && (
                            <div className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                              {c.oneliner}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {c.industries.length === 0 ? (
                        '—'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {c.industries.map((i) => (
                            <span
                              key={i.id}
                              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
                            >
                              {i.emoji && <span>{i.emoji}</span>}
                              <span>{i.name}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{c.batch_code ?? '—'}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {c.country ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span>{c.country.flag}</span>
                          <span>{c.country.name}</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={STATUS_VARIANT[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="mt-4">
          <Pagination
            basePath={companiesPath()}
            currentPage={pagination.current_page}
            nextPage={pagination.next_page}
            prevPage={pagination.prev_page}
            totalPages={pagination.total_pages}
            query={paginationQuery as Record<string, string | number | string[] | null | undefined>}
          />
        </div>
      </div>
    </>
  )
}

export default Object.assign(CompaniesIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
